import React from 'react';

/* ==========================================================================
   AxonMetric module icons
   --------------------------------------------------------------------------
   A purpose-drawn set for the site navigation modules. Replaces a mix of
   stock PNGs and generic lucide glyphs (two modules previously shared the
   same `Activity` icon, so they were indistinguishable).

   Shared construction so the set reads as one family:
     * 24x24 viewBox, geometry kept on a 2px grid
     * 1.6 stroke, round caps and joins, no fills except deliberate accents
     * `currentColor` throughout, so the brand palette drives colour and a
       single icon works on light, tinted and inverted surfaces
     * one "accent" element per icon at 0.45 opacity, giving each glyph a
       focal point without introducing a second hue
   ========================================================================== */

export interface ModuleIconProps {
  size?: number;
  className?: string;
  strokeWidth?: number;
}

const base = (size: number, strokeWidth: number, className?: string) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className,
  'aria-hidden': true,
  focusable: false as const,
});

/* Performance Analytics — measured bars under a rising trend line. */
export const PerformanceAnalyticsIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M3 20h18" />
    <rect x="5" y="12" width="3.2" height="5" rx="0.6" opacity="0.45" />
    <rect x="10.4" y="9" width="3.2" height="8" rx="0.6" opacity="0.45" />
    <rect x="15.8" y="5.5" width="3.2" height="11.5" rx="0.6" opacity="0.45" />
    <path d="M4.5 9.5 9 6l3.5 2.6L19.5 3" />
    <circle cx="19.5" cy="3" r="1.3" />
  </svg>
);

/* Generation Reports — a report sheet carrying a plotted figure. */
export const GenerationReportsIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M14 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8z" />
    <path d="M14 3v4a1 1 0 0 0 1 1h4" opacity="0.45" />
    <path d="M8.5 16.5 11 13.5l2 1.8 2.5-3.6" />
    <path d="M8.5 10h3" opacity="0.45" />
  </svg>
);

/* Live Dashboards — a gauge with a live needle. */
export const LiveDashboardsIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M3.5 18a9 9 0 1 1 17 0" />
    <path d="M12 18l4.2-5.2" />
    <circle cx="12" cy="18" r="1.5" />
    <path d="M6.2 13.2h1.6M16.2 13.2h1.6M11.2 7.6h1.6" opacity="0.45" />
  </svg>
);

/* O&M — a service gear paired with a maintenance spanner. */
export const OperationsMaintenanceIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <circle cx="8.6" cy="8.6" r="3.1" opacity="0.45" />
    <path
      d="M8.6 3.1V1.9M8.6 15.3v1.2M3.1 8.6H1.9M15.3 8.6h1.2M4.7 4.7 3.9 3.9M12.5 12.5l.8.8M12.5 4.7l.8-.8M4.7 12.5l-.8.8"
      opacity="0.45"
    />
    <path d="M19.9 13.1a3.4 3.4 0 0 0-4.4 4.4l-3.6 3.6a1.4 1.4 0 0 0 2 2l3.6-3.6a3.4 3.4 0 0 0 4.4-4.4l-1.9 1.9-1.9-.5-.5-1.9z" />
  </svg>
);

/* Upload Plant Data — a data store receiving an upload. */
export const UploadPlantDataIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <ellipse cx="12" cy="6" rx="7" ry="2.8" opacity="0.45" />
    <path d="M5 6v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8V6" opacity="0.45" />
    <path d="M5 12v5c0 1.5 3.1 2.8 7 2.8" opacity="0.45" />
    <path d="M17 21v-6" />
    <path d="M14.4 17.6 17 15l2.6 2.6" />
  </svg>
);

/* Alerts Portal — a bell emitting a signal. */
export const AlertsPortalIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M17 10a5 5 0 0 0-10 0c0 4.4-1.6 5.7-1.6 5.7h13.2S17 14.4 17 10" />
    <path d="M10.4 19a1.9 1.9 0 0 0 3.2 0" />
    <path d="M19.4 5.2a7.4 7.4 0 0 1 1.7 3M4.6 5.2a7.4 7.4 0 0 0-1.7 3" opacity="0.45" />
  </svg>
);

/* Simulation-On-Demand — tunable parameters driving a run. */
export const SimulationIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M4 7h7M15.5 7H20M4 12h3.5M12 12h8M4 17h7M15.5 17H20" opacity="0.45" />
    <circle cx="13" cy="7" r="1.9" />
    <circle cx="9.5" cy="12" r="1.9" />
    <circle cx="13" cy="17" r="1.9" />
  </svg>
);

/* DSM — scheduled blocks with a dispatch pulse. */
export const DSMIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <rect x="3" y="4.5" width="18" height="15.5" rx="1.6" opacity="0.45" />
    <path d="M3 9h18M8 4.5V2.8M16 4.5V2.8" opacity="0.45" />
    <path d="M13.2 11.6 10 15.4h2.6L11.4 18.8 15 14.8h-2.7z" />
  </svg>
);

/* Inventory Management — stocked shelving. */
export const InventoryIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <rect x="3" y="3.5" width="8" height="7" rx="0.8" />
    <rect x="13" y="3.5" width="8" height="7" rx="0.8" opacity="0.45" />
    <rect x="3" y="13.5" width="8" height="7" rx="0.8" opacity="0.45" />
    <rect x="13" y="13.5" width="8" height="7" rx="0.8" />
    <path d="M5.6 7h2.8M15.6 17h2.8" opacity="0.45" />
  </svg>
);

/* Vendor Management — a supplier relationship and its paperwork. */
export const VendorIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M3 20V8.5L9 5v15" opacity="0.45" />
    <path d="M9 20h12V11l-6-2.4" />
    <path d="M5.4 11h1.4M5.4 14.4h1.4M12.4 13.4h1.6M17 13.4h1.6M12.4 16.6h1.6M17 16.6h1.6" opacity="0.45" />
  </svg>
);

/* Documentation — a controlled checklist. */
export const DocumentationIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M7 3.5h10A1.5 1.5 0 0 1 18.5 5v15A1.5 1.5 0 0 1 17 21.5H7A1.5 1.5 0 0 1 5.5 20V5A1.5 1.5 0 0 1 7 3.5" opacity="0.45" />
    <path d="M9.5 2.6h5a1 1 0 0 1 1 1v1.2a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1V3.6a1 1 0 0 1 1-1" />
    <path d="M8.8 11.4l1.5 1.5 2.9-2.9" />
    <path d="M8.8 16.6h6.4" opacity="0.45" />
  </svg>
);

/* Home — return to the site navigation grid. */
export const HomeIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M3.5 10.4 12 3.6l8.5 6.8" />
    <path d="M5.6 12v7.4a1 1 0 0 0 1 1h10.8a1 1 0 0 0 1-1V12" opacity="0.45" />
    <path d="M9.8 20.4v-5.1a1 1 0 0 1 1-1h2.4a1 1 0 0 1 1 1v5.1" />
  </svg>
);

/* Site Information — the plant and its reference data. */
export const SiteInformationIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M3.5 20.5h17" />
    <path d="M6 20.5V9.4l6-4.2 6 4.2v11.1" opacity="0.45" />
    <path d="M12 12.4v4.6" />
    <circle cx="12" cy="9.8" r="0.9" />
  </svg>
);

/* Compliance — a verified, controlled record. */
export const ComplianceIcon = ({ size = 20, className, strokeWidth = 1.6 }: ModuleIconProps) => (
  <svg {...base(size, strokeWidth, className)}>
    <path d="M12 2.8 4.8 5.6v6c0 4.4 3 8.3 7.2 9.6 4.2-1.3 7.2-5.2 7.2-9.6v-6z" opacity="0.45" />
    <path d="m8.8 11.9 2.3 2.3 4.1-4.5" />
  </svg>
);

/* Registry keyed by moduleKey, for lookup from navigation config. */
export const MODULE_ICONS: Record<string, React.FC<ModuleIconProps>> = {
  home: HomeIcon,
  site_information: SiteInformationIcon,
  compliance: ComplianceIcon,
  dashboard: PerformanceAnalyticsIcon,
  generation_reports: GenerationReportsIcon,
  live_dashboards: LiveDashboardsIcon,
  om: OperationsMaintenanceIcon,
  upload: UploadPlantDataIcon,
  alerts: AlertsPortalIcon,
  simulation: SimulationIcon,
  dsm: DSMIcon,
  inventory: InventoryIcon,
  vendors: VendorIcon,
  documentation: DocumentationIcon,
};

export default MODULE_ICONS;
